import React from 'react';
import type { FunctionComponent } from 'react';
import type { RosterManagementStudentWithGuardiansListItem, StaffGet } from '@whizz/api-model';
import type { UseQueryResult } from 'react-query';
import { useI18n } from '@whizz/react-i18n';
import { useQuery } from 'react-query';
import { repository } from '@whizz/api-client';
import { RosterManagementStudentActionEnum } from '@whizz/api-model';
import { Link } from 'react-router-dom';
import { GlobalDashboardLayout } from '../../layouts';
import { QUERIES_IDS, ROSTER_ROUTES, ROUTES } from '../../util/vars';
import WelcomeCard from './WelcomeCard';
import WelcomeFooterTip from './WelcomeFooterTip';
import Loader from '../../components/loader/Loader';
import StudentAccountsIcon from '../../assets/welcome/student-accounts.png';
import StaffAccountsIcon from '../../assets/welcome/staff-accounts.png';
import ParentEmailsIcon from '../../assets/welcome/parent-emails.png';

type Props = {
  hasTraining: boolean;
  isNz: boolean;
  textAlign: string;
  staffQuery: UseQueryResult<StaffGet>;
  classSelector: React.ReactNode;
};

const CTA_CLASS = 'rounded-lg font-bold text-base mt-auto self-start px-5 py-2.5';
const ILEARN_SUPPORT_SITE_URL = 'https://support.ilearn.co.nz/login';

const WelcomeAdmin: FunctionComponent<Props> = ({ hasTraining, isNz, textAlign, staffQuery, classSelector }) => {
  const { translate } = useI18n();

  const studentsStatisticsQuery = useQuery(
    [ QUERIES_IDS.ROSTER_MANAGEMENT.STUDENTS_STATISTICS ],
    () => repository.RosterManagementRepository.studentsStatistics(),
  );

  const guardiansQuery = useQuery(
    [ QUERIES_IDS.ROSTER_MANAGEMENT.GUARDIANS ],
    () => repository.StudentRepository.rosterManagementListItemsByAction(
      RosterManagementStudentActionEnum.guardians,
    ),
  );

  const totalStudents = studentsStatisticsQuery.data?.totalStudents ?? 0;
  const totalStaff = staffQuery.data?.teachers.length ?? 0;
  const totalParents = (guardiansQuery.data as unknown as RosterManagementStudentWithGuardiansListItem[] ?? [])
    .reduce((total, student) => total + student.guardians.length, 0);

  const isStudentsLoading = !studentsStatisticsQuery.isFetched || studentsStatisticsQuery.isFetching;
  const isStaffLoading = !staffQuery.isFetched || staffQuery.isFetching;
  const isParentsLoading = !guardiansQuery.isFetched || guardiansQuery.isFetching;

  return (
    <GlobalDashboardLayout disableContainer actionContent={classSelector}>
      <div className={'mx-auto space-y-4 max-w-4xl'} data-cy={'welcome-page'}>
        <WelcomeCard variant={'admin'}>
          <div className={'mb-2.5 font-extrabold uppercase text-header text-xs tracking-wider'}>
            {translate('pages.WELCOME.ADMIN.HERO.EYEBROW')}
          </div>
          <h1 className={'mb-3.5 font-extrabold text-gray-900 text-3xl leading-tight'}>
            {translate('pages.WELCOME.ADMIN.HERO.TITLE')}
          </h1>
          <p className={`text-gray-600 text-lg leading-relaxed ${textAlign}`}>
            {translate(hasTraining
              ? 'pages.WELCOME.ADMIN.HERO.DESCRIPTION'
              : 'pages.WELCOME.ADMIN.HERO.DESCRIPTION_NO_TRAINING')}
          </p>
        </WelcomeCard>

        <WelcomeCard variant={'admin'}>
          <div className={'grid grid-cols-1 md:grid-cols-3 gap-4 mb-7'}>
            <div
              className={'flex items-center gap-2.5 p-3.5 border border-gray-200 rounded-lg shadow-sm'}
              data-cy={'welcome-stat-students'}
            >
              <div>
                {isStudentsLoading
                  ? <Loader disableLabel smallSize noMargin />
                  : <div className={'inline-flex font-extrabold text-black text-xl rounded-default bg-header py-1 px-2 mb-1'}>{totalStudents}</div>}
                <div className={'font-bold text-black text-base leading-tight'}>{translate('pages.WELCOME.ADMIN.STATS.STUDENTS')}</div>
              </div>
              <img src={StudentAccountsIcon} alt={''} className={'flex-none object-contain ml-auto w-11 h-11'} />
            </div>
            <div
              className={'flex items-center gap-2.5 p-3.5 border border-gray-200 rounded-lg shadow-sm'}
              data-cy={'welcome-stat-staff'}
            >
              <div>
                {isStaffLoading
                  ? <Loader disableLabel smallSize noMargin />
                  : <div className={'inline-flex font-extrabold text-black text-xl rounded-default bg-header py-1 px-2 mb-1'}>{totalStaff}</div>}
                <div className={'font-bold text-black text-base leading-tight'}>{translate('pages.WELCOME.ADMIN.STATS.STAFF')}</div>
              </div>
              <img src={StaffAccountsIcon} alt={''} className={'flex-none object-contain ml-auto w-14 h-14'} />
            </div>
            <div
              className={'flex items-center gap-2.5 p-3.5 border border-gray-200 rounded-lg shadow-sm'}
              data-cy={'welcome-stat-parents'}
            >
              <div>
                {isParentsLoading
                  ? <Loader disableLabel smallSize noMargin />
                  : <div className={'inline-flex font-extrabold text-black text-xl rounded-default bg-header py-1 px-2 mb-1'}>{totalParents}</div>}
                <div className={'font-bold text-black text-base leading-tight'}>{translate('pages.WELCOME.ADMIN.STATS.PARENTS')}</div>
              </div>
              <img src={ParentEmailsIcon} alt={''} className={'flex-none object-contain ml-auto w-14 h-14'} />
            </div>
          </div>

          <div className={`grid grid-cols-1 ${hasTraining ? 'lg:grid-cols-2' : ''} gap-5`}>
            <div
              className={'flex flex-col p-5 rounded-xl bg-header bg-opacity-20 border border-header border-opacity-30'}
              data-cy={'welcome-step-get-started'}
            >
              <div className={'flex items-center gap-3.5 mb-4'}>
                {hasTraining && (
                  <span
                    className={'flex-none w-7 h-7 rounded-full text-black flex items-center justify-center font-extrabold bg-header text-sm'}
                  >1</span>
                )}
                <span className={'font-extrabold uppercase text-header text-xs tracking-wider'}>
                  {translate('pages.WELCOME.ADMIN.STEP_GET_STARTED.LABEL')}
                </span>
              </div>
              <h2 className={'mb-2.5 font-extrabold text-xl text-gray-900'}>
                {translate('pages.WELCOME.ADMIN.STEP_GET_STARTED.TITLE')}
              </h2>
              <p className={`mb-5 text-base text-gray-600 leading-relaxed ${hasTraining ? `max-w-sm ${textAlign}` : 'text-left'}`}>
                {translate('pages.WELCOME.ADMIN.STEP_GET_STARTED.DESCRIPTION')}
              </p>
              {isNz && (
                <p className={`mb-5 text-base text-gray-600 leading-relaxed ${hasTraining ? `max-w-sm ${textAlign}` : 'text-left'}`}>
                  {translate('pages.WELCOME.ADMIN.STEP_GET_STARTED.DESCRIPTION_STAFF_PREFIX')}{' '}
                  <a href={ILEARN_SUPPORT_SITE_URL} target={'_blank'} rel={'noreferrer'} className={'underline'}>
                    {translate('pages.WELCOME.ADMIN.STEP_GET_STARTED.DESCRIPTION_STAFF_LINK')}
                  </a>.
                </p>
              )}
              {hasTraining && (
                <p className={`mb-5 text-base text-gray-600 leading-relaxed max-w-sm ${textAlign}`}>
                  {translate('pages.WELCOME.ADMIN.STEP_GET_STARTED.DESCRIPTION_SKIP')}
                </p>
              )}
              <Link to={`${ROUTES.roster_management}${ROSTER_ROUTES.upload}`} className={`${CTA_CLASS} bg-header text-black`} data-cy={'welcome-cta-upload'}>
                {translate('pages.WELCOME.ADMIN.STEP_GET_STARTED.CTA')}
              </Link>
            </div>

            {hasTraining && (
              <div
                className={'flex flex-col p-5 rounded-xl bg-gray-50 border border-gray-200'}
                data-cy={'welcome-step-training'}
              >
                <div className={'flex items-center gap-3.5 mb-4'}>
                  <span
                    className={'flex-none w-7 h-7 rounded-full text-white flex items-center justify-center font-extrabold bg-primary text-sm'}
                  >2</span>
                  <span className={'font-extrabold uppercase text-primary text-xs tracking-wider'}>
                    {translate('pages.WELCOME.ADMIN.STEP_TRAINING.LABEL')}
                  </span>
                </div>
                <h2 className={'mb-2.5 font-extrabold text-xl text-gray-900'}>
                  {translate('pages.WELCOME.ADMIN.STEP_TRAINING.TITLE')}
                </h2>
                <p className={`mb-3.5 text-base text-gray-600 leading-relaxed max-w-sm ${textAlign}`}>
                  {translate('pages.WELCOME.ADMIN.STEP_TRAINING.DESCRIPTION')}
                </p>
                <p className={`mb-3.5 text-base text-gray-600 leading-relaxed max-w-sm ${textAlign}`}>
                  {translate('pages.WELCOME.ADMIN.STEP_TRAINING.DESCRIPTION_2')}
                </p>
                <Link to={ROUTES.training} className={`${CTA_CLASS} bg-primary text-white`} data-cy={'welcome-cta-training'}>
                  {translate('pages.WELCOME.ADMIN.STEP_TRAINING.CTA')}
                </Link>
              </div>
            )}
          </div>
        </WelcomeCard>

        <WelcomeFooterTip variant={'admin'} isNz={isNz}>
          <div className={`text-base text-gray-600 leading-relaxed ${textAlign}`}>
            <span className={'font-bold'}>{translate('pages.WELCOME.ADMIN.FOOTER.ALREADY_SET_UP_PREFIX')}</span>{' '}
            <Link to={ROUTES.school_reports} className={'underline'}>{translate('pages.WELCOME.ADMIN.FOOTER.SCHOOL_REPORTS_LABEL')}</Link>{' '}
            {translate('pages.WELCOME.ADMIN.FOOTER.ALREADY_SET_UP_SUFFIX')}
          </div>
        </WelcomeFooterTip>
      </div>
    </GlobalDashboardLayout>
  );
};

export default WelcomeAdmin;
